import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d69srcua728k8ctu2tv0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/b5996fb417b605dd6c9a4cc32cf8c0bf.jpg" 
          alt="Contact L&A Contract Flooring"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-7xl font-bold text-white uppercase tracking-wide mb-6">
            READY TO TRANSFORM YOUR SPACE?
          </h2>
          <p className="text-xl md:text-2xl text-white mb-2">Get Your Free Technical Consultation & Formal Proposal</p>
          <p className="text-lg md:text-xl text-white/90">Licensed, Insured & Trusted Across Florida</p>
        </div>

        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="(321) 201-5387"
                />
              </div>
              <div>
                <label htmlFor="projectType" className="block text-sm font-semibold text-gray-700 mb-2">Project Type *</label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select project type</option>
                  <option value="Corporate Carpet">Corporate Carpet</option>
                  <option value="Luxury Vinyl Plank">Luxury Vinyl Plank (LVP)</option>
                  <option value="Laminate">Laminate</option>
                  <option value="Hardwood">Hardwood</option>
                  <option value="Subfloor Preparation">Subfloor Preparation</option>
                  <option value="Consultation">Technical Consultation</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Project Details *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, message: e.target.value });
                  }
                }}
                required
                rows={5}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all resize-none text-sm"
                placeholder="Please provide details about your project including square footage, timeline, and any specific requirements..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-[#FF6B35] text-white text-base font-semibold rounded-full hover:bg-[#FF6B35]/90 transition-all flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span>Submitting...</span>
              ) : (
                <>
                  <span>REQUEST FREE CONSULTATION</span>
                  <i className="ri-send-plane-fill text-xl"></i>
                </>
              )}
            </button>

            {submitStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-center">
                <i className="ri-checkbox-circle-fill text-2xl mr-2"></i>
                Thank you! We'll contact you within 24 hours.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-center">
                <i className="ri-error-warning-fill text-2xl mr-2"></i>
                Something went wrong. Please try again or call us directly.
              </div>
            )}
          </form>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="w-12 h-12 flex items-center justify-center bg-[#2E5C8A] rounded-full mx-auto mb-3">
                  <i className="ri-phone-line text-xl text-white"></i>
                </div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Call Us</p>
                <a href="tel:321-201-5387" className="text-[#FF6B35] font-bold hover:text-[#FF6B35]/80 cursor-pointer">321-201-5387</a>
              </div>
              <div>
                <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B35] rounded-full mx-auto mb-3">
                  <i className="ri-mail-line text-xl text-white"></i>
                </div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Email Us</p>
                <a href="mailto:Estimating@lacontractflooring.com" className="text-[#2E5C8A] font-bold hover:text-[#2E5C8A]/80 cursor-pointer break-all">Estimating@lacontractflooring.com</a>
              </div>
              <div>
                <div className="w-12 h-12 flex items-center justify-center bg-gray-400 rounded-full mx-auto mb-3">
                  <i className="ri-map-pin-line text-xl text-white"></i>
                </div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Visit Us</p>
                <p className="text-gray-600 text-sm">160 W. Camino Real Unit #843<br />Boca Raton, FL 33432</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
